import { IFilterList } from '@/utils/interfaces/FilterListInterface'
import {toPartialQueryString} from '@/utils/parsers/QueryString'
import { IOrderStatusListEnumDescription, IOrderStatusListTrackingCodeEnumDescription, IOrderStatusListIdSourceEnumDescription } from '@/order/interfaces/OrderInterfaces'
import { IDateRange } from '@/utils/interfaces/DateRangeInterface'
import { OrderStatusEnum, getOrderStatusEnumDescription } from '@c2b/muven-commons'
import { ChannelModel } from '@/channel/models/ChannelModel'
import { OrderStatusTrackingCodeEnum } from '../enums/OrderStatusTrackingCodeEnum'
import { OrderStatusIdSourceEnum } from '../enums/OrderStatusIdSourceEnum'
import { StoreModel } from '../../store/models/StoreModel'
import { ProductModel } from '../../product/models/ProductModel'

export class OrderListFilterModel implements IFilterList {
    static statusList():IOrderStatusListEnumDescription[] {
        return [
            {enum: OrderStatusEnum.Error, description: getOrderStatusEnumDescription(OrderStatusEnum.Error)},
            {enum: OrderStatusEnum.PendingAssignments, description: getOrderStatusEnumDescription(OrderStatusEnum.PendingAssignments)},
            {enum: OrderStatusEnum.Pending, description: getOrderStatusEnumDescription(OrderStatusEnum.Pending)},
            {enum: OrderStatusEnum.Approved, description: getOrderStatusEnumDescription(OrderStatusEnum.Approved)},
            {enum: OrderStatusEnum.Billed, description: getOrderStatusEnumDescription(OrderStatusEnum.Billed)},
            {enum: OrderStatusEnum.Posted, description: getOrderStatusEnumDescription(OrderStatusEnum.Posted)},
            {enum: OrderStatusEnum.Delivered, description: getOrderStatusEnumDescription(OrderStatusEnum.Delivered)},
            {enum: OrderStatusEnum.Canceled, description: getOrderStatusEnumDescription(OrderStatusEnum.Canceled)},
            {enum: OrderStatusEnum.Finalized, description: getOrderStatusEnumDescription(OrderStatusEnum.Finalized)},
            {enum: OrderStatusEnum.Integrating, description: getOrderStatusEnumDescription(OrderStatusEnum.Integrating)},
        ]
    }

    static statusTrackingCodeList():IOrderStatusListTrackingCodeEnumDescription[] {
        return [
            {enum: OrderStatusTrackingCodeEnum.All, description: 'Pedidos COM e SEM código'},
            {enum: OrderStatusTrackingCodeEnum.WithTrackingCode, description: 'Apenas pedidos COM código'},
            {enum: OrderStatusTrackingCodeEnum.WitoutTrackingCode, description: 'Apenas pedidos SEM código'},
        ]
    }

    static statusIdSourceList():IOrderStatusListIdSourceEnumDescription[] {
        return [
            {enum: OrderStatusIdSourceEnum.All, description: 'Pedidos IMPORTADOS e NÃO IMPORTADOS pela retaguarda'},
            {enum: OrderStatusIdSourceEnum.WithIdSource, description: 'Apenas pedidos IMPORTADOS pela retaguarda'},
            {enum: OrderStatusIdSourceEnum.WithoutIdSource, description: 'Apenas pedidos NÃO IMPORTADOS pela retaguarda'},
        ]
    }

    custom: boolean = false
    idSource:string = ''
    idExternalChannel:string = ''
    customer:string = ''
    accessKey:string = ''
    trackingCode: string = ''
    statusTrackingCode?:IOrderStatusListTrackingCodeEnumDescription = undefined
    statusIdSource?:IOrderStatusListIdSourceEnumDescription = undefined
    storeList: StoreModel[] = []
    productIdList: ProductModel[] = []
    date:IDateRange = {start: null, end: null}
    dateCreatedAtMuven:IDateRange = {start: null, end: null}
    channelList:ChannelModel[] = []
    statusList:IOrderStatusListEnumDescription[] = []

    constructor(o?:Partial<OrderListFilterModel>) {
        this._normalizeWhenCustom(o)
        Object.assign(this, o)
   }

    hasTooSpecificFilter():boolean {
        return !!this.accessKey ||
               !!this.trackingCode ||
               !!this.idSource ||
               !!this.idExternalChannel
    }

    clearStatusList():void {
        this.statusList = []
    }

    isFilterFilled():boolean {
        return !!this.idSource ||
               !!this.idExternalChannel ||
               !!this.customer ||
               !!this.accessKey ||
               !!this.date.start ||
               !!this.date.end ||
               !!this.dateCreatedAtMuven.start ||
               !!this.dateCreatedAtMuven.end ||
               !!this.channelList.length ||
               !!this.trackingCode.length ||
               !!(this.statusTrackingCode && this.statusTrackingCode.enum !== undefined && this.statusTrackingCode !== null) ||
               !!(this.statusIdSource && this.statusIdSource.enum !== undefined && this.statusIdSource !== null) ||
               !!this.statusList.length ||
               !!this.productIdList.length ||
               !!this.storeList.length
    }

    toPartialQueryString():string {
        const startDate = this.date ? this.date.start as Date : null
        const endDate = this.date ? this.date.end as Date : null

        const startDateCreatedAtMuven = this.dateCreatedAtMuven ? this.dateCreatedAtMuven.start as Date : null
        const endDateCreatedAtMuven = this.dateCreatedAtMuven ? this.dateCreatedAtMuven.end as Date : null

        const tmp = {
            ...this,
            date: undefined,
            dateCreatedAtMuven: undefined,
            trackingCode: this.trackingCode,
            statusTrackingCode: this.statusTrackingCode ? this.statusTrackingCode.enum : undefined,
            statusIdSource: this.statusIdSource ? this.statusIdSource.enum : undefined,
            startDate: startDate ? startDate.toISOString() : undefined,
            endDate: endDate ? endDate.toISOString() : undefined,
            startDateCreatedAtMuven: startDateCreatedAtMuven ? startDateCreatedAtMuven.toISOString() : undefined,
            endDateCreatedAtMuven: endDateCreatedAtMuven ? endDateCreatedAtMuven.toISOString() : undefined,
            storeList: this.storeList.map((s) => {
                return s.id
            }),
            channelList: this.channelList.map((c) => {
                return c.id
            }),
            statusList: this.statusList.map((s) => {
                return s.enum
            }),
            productIdList: this.productIdList.map((p) => {
                return p.id
            }),
        }

        return toPartialQueryString(tmp)
    }

    private _normalizeWhenCustom(o?:Partial<OrderListFilterModel>):void {
        if (!!o && o.custom) {
            let statusListEnum:string[] = []
            let productIdList:string[] = []
            
            if (typeof o.statusList === "string") {
                statusListEnum = (o.statusList as unknown as string).split(',')
                o.statusList = []
            }

            if (typeof o.productIdList === "string") {
                productIdList = (o.productIdList as unknown as string).split(',')
                o.productIdList = []
            }

            for (const item of statusListEnum) {
                o.statusList!.push({
                    enum: Number(item) as unknown as OrderStatusEnum,
                    description: getOrderStatusEnumDescription(Number(item) as unknown as OrderStatusEnum)
                })
            }

            for (const id of productIdList) {
                o.productIdList!.push({
                    id,
                    name: '',
                } as ProductModel)
            }

            if (!!o.statusIdSource) {
                const statusIdSource:number = Number(o.statusIdSource)

                if (statusIdSource === OrderStatusIdSourceEnum.All) {
                    o.statusIdSource = {
                        enum: OrderStatusIdSourceEnum.All,
                        description: 'Pedidos IMPORTADOS e NÃO IMPORTADOS pela retaguarda',
                    }
                } else if (statusIdSource === OrderStatusIdSourceEnum.WithIdSource) {
                    o.statusIdSource = {
                        enum: OrderStatusIdSourceEnum.WithIdSource,
                        description: 'Apenas pedidos IMPORTADOS pela retaguarda',
                    }
                } else if (statusIdSource === OrderStatusIdSourceEnum.WithoutIdSource) {
                    o.statusIdSource = {
                        enum: OrderStatusIdSourceEnum.WithoutIdSource,
                        description: 'Apenas pedidos NÃO IMPORTADOS pela retaguarda',
                    }
                }
            }
        }
    }
}
